/* eslint-disable react-hooks/set-state-in-effect -- known false positive (react#34905) on async event handlers; not enforced by the app's build toolchain. */
import { useEffect, useState, useCallback } from "react";
import { toast } from "sonner";
import { Plus } from "lucide-react";
import adminApi from "@/lib/adminApi";
import { Field, Input, Btn, StatusPill } from "@/admin/components";
import { formatINR, formatDate } from "@/lib/format";

const METHODS = [["upi", "UPI"], ["bank", "Bank transfer"], ["cash", "Cash"], ["razorpay_link", "Razorpay link"], ["offline", "Other"]];

const Row = ({ label, value }) => (
  <div className="flex items-center justify-between py-0.5">
    <span className="text-[#666]">{label}</span>
    <span className="font-medium text-[#111]">{value}</span>
  </div>
);

export const SubscriptionBlock = ({ astroId }) => {
  const [sub, setSub] = useState(null);
  const [open, setOpen] = useState(false);
  const [amount, setAmount] = useState("");
  const [method, setMethod] = useState("upi");
  const [note, setNote] = useState("");

  const load = useCallback(async () => {
    const { data } = await adminApi.get(`/admin/astrologers/${astroId}/subscription`);
    setSub(data);
    setAmount(String(data.price));
  }, [astroId]);

  useEffect(() => { load(); }, [load]);

  if (!sub) return <p className="text-[13px] text-[#999]">Loading…</p>;

  const expired = sub.paid_until && sub.paid_until < new Date().toISOString().slice(0, 10);

  return (
    <div className="space-y-2.5 text-[14px]" data-testid="admin-subscription-block">
      <Row label="Status" value={<StatusPill status={sub.status} />} />
      {sub.lifetime && <Row label="Plan" value="Lifetime" />}
      {sub.billing_source && <Row label="Source" value={sub.billing_source === "razorpay_autopay" ? "Razorpay AutoPay" : "Manual / pay-link"} />}
      {sub.paid_until && (
        <Row label={expired ? "Expired on" : "Active until"} value={<span className={expired ? "text-[#c0392b]" : "text-[#111]"} data-testid="subscription-paid-until">{formatDate(sub.paid_until)}</span>} />
      )}

      {!open ? (
        <button
          data-testid="record-payment-btn"
          onClick={() => setOpen(true)}
          className="mt-1 flex items-center gap-1.5 rounded-[10px] border border-[#e9e6e1] bg-white px-3 py-2 text-[13px] font-semibold text-[#111] transition-colors hover:bg-[#faf8f5]"
        >
          <Plus size={14} strokeWidth={2.2} /> Record payment (+1 month)
        </button>
      ) : (
        <div className="mt-2 space-y-3 rounded-[12px] border border-[#eee] bg-[#faf9f7] p-3.5" data-testid="record-payment-form">
          <div className="grid grid-cols-2 gap-3">
            <Field label="Amount (₹)"><Input data-testid="record-amount" type="number" value={amount} onChange={(e) => setAmount(e.target.value)} /></Field>
            <Field label="Method">
              <select data-testid="record-method" value={method} onChange={(e) => setMethod(e.target.value)}
                className="h-11 w-full rounded-[10px] border border-[#e5e5e5] bg-white px-3 text-[14px] outline-none">
                {METHODS.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
              </select>
            </Field>
          </div>
          <Field label="Note (optional)"><Input data-testid="record-note" value={note} onChange={(e) => setNote(e.target.value)} placeholder="Reference / remark" /></Field>
          <div className="flex gap-2">
            <Btn
              testid="record-save"
              onClick={async () => {
                const amt = parseInt(amount, 10);
                if (Number.isNaN(amt) || amt < 1) { toast.error("Enter a valid amount"); return; }
                try {
                  const { data } = await adminApi.post(`/admin/astrologers/${astroId}/subscription/record-payment`, { amount_inr: amt, method, note });
                  toast.success(`Payment recorded — active until ${formatDate(data.paid_until)}`);
                  setOpen(false); setNote("");
                  await load();
                } catch (e) {
                  toast.error(e?.response?.data?.detail || "Couldn't record payment");
                }
              }}
            >Save payment</Btn>
            <button data-testid="record-cancel" onClick={() => setOpen(false)} className="rounded-[10px] px-3 text-[13px] font-medium text-[#777] hover:text-[#111]">Cancel</button>
          </div>
        </div>
      )}

      {sub.payments?.length > 0 && (
        <div className="mt-2 border-t border-[#f2efea] pt-2.5" data-testid="payment-history">
          <p className="mb-1.5 text-[11px] font-semibold uppercase tracking-[0.06em] text-[#999]">Payment history</p>
          <div className="space-y-1">
            {sub.payments.map((p) => (
              <div key={p.id} className="flex items-center justify-between text-[13px]">
                <span className="text-[#555]">{formatDate(String(p.created_at).slice(0, 10))} · {p.method}{p.recorded_by && p.recorded_by !== "self" ? " (admin)" : ""}</span>
                <span className="font-medium text-[#111]">{formatINR(p.amount_inr)}</span>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};
